/**
 * Agents feature Zustand store
 * Manages agents list state, live sessions, and session subscriptions
 */

import { create } from 'zustand'
import * as agentsApi from './api'
import type {
  AgentDetail,
  AgentBlueprint,
  LiveSession,
  SessionEvent,
  AgentsViewAgent,
} from './types'

interface AgentsState {
  // Agent list
  agents: AgentsViewAgent[]
  selectedAgent: AgentDetail | null

  // Agent blueprint (full identity introspection)
  blueprint: AgentBlueprint | null
  isLoadingBlueprint: boolean

  // Live sessions
  liveSessions: LiveSession[]
  subscribedSessionId: string | null
  sessionEvents: SessionEvent[]

  // Loading/error states
  isLoading: boolean
  isLoadingAgent: boolean
  isLoadingLiveSessions: boolean
  error: string | null
  blueprintError: string | null
}

interface AgentsActions {
  // Agent actions
  fetchAgents: () => Promise<void>
  fetchAgentDetail: (agentId: string) => Promise<void>
  fetchBlueprint: (agentId: string, includeLayerContent?: boolean) => Promise<void>
  clearSelectedAgent: () => void
  clearBlueprint: () => void

  // Live session actions
  fetchLiveSessions: () => Promise<void>
  subscribeToSession: (sessionId: string) => Promise<void>
  unsubscribeFromSession: () => Promise<void>
  addSessionEvent: (event: SessionEvent) => void
  clearSessionEvents: () => void

  // Error handling
  setError: (error: string | null) => void
  clearError: () => void
}

/**
 * Module-level AbortController for the active SSE session stream.
 * Stored outside Zustand state because it is not serialisable and
 * must persist across re-renders without triggering store updates.
 */
let _sessionAbortController: AbortController | null = null

export const useAgentsStore = create<AgentsState & AgentsActions>((set, get) => ({
  // Initial state
  agents: [],
  selectedAgent: null,
  blueprint: null,
  isLoadingBlueprint: false,
  liveSessions: [],
  subscribedSessionId: null,
  sessionEvents: [],
  isLoading: false,
  isLoadingAgent: false,
  isLoadingLiveSessions: false,
  error: null,
  blueprintError: null,

  // Fetch all agents
  fetchAgents: async () => {
    set({ isLoading: true, error: null })
    try {
      const agents = await agentsApi.fetchAgents()
      set({ agents, isLoading: false })
    } catch (e) {
      set({ error: String(e), isLoading: false })
    }
  },

  // Fetch single agent detail
  fetchAgentDetail: async (agentId: string) => {
    set({ isLoadingAgent: true, error: null })
    try {
      const agent = await agentsApi.fetchAgentDetail(agentId)
      set({ selectedAgent: agent, isLoadingAgent: false })
    } catch (e) {
      set({ error: String(e), isLoadingAgent: false })
    }
  },

  clearSelectedAgent: () => {
    set({ selectedAgent: null })
  },

  // Fetch full agent blueprint
  fetchBlueprint: async (agentId: string, includeLayerContent = false) => {
    set({ isLoadingBlueprint: true, blueprintError: null })
    try {
      const blueprint = await agentsApi.fetchAgentBlueprint(agentId, includeLayerContent)
      set({ blueprint, isLoadingBlueprint: false })
    } catch (e) {
      set({ blueprintError: String(e), isLoadingBlueprint: false })
    }
  },

  clearBlueprint: () => {
    set({ blueprint: null, blueprintError: null })
  },

  // Fetch live sessions
  fetchLiveSessions: async () => {
    set({ isLoadingLiveSessions: true, error: null })
    try {
      const sessions = await agentsApi.fetchLiveSessions()
      set({ liveSessions: sessions, isLoadingLiveSessions: false })
    } catch (e) {
      console.error('Failed to fetch live sessions:', e)
      set({ error: String(e), isLoadingLiveSessions: false })
    }
  },

  // Subscribe to a session's live event stream.
  // Discovers the agent_id from the liveSessions list so the caller
  // only needs to pass session_id (matching the existing interface).
  subscribeToSession: async (sessionId: string) => {
    // Unsubscribe from previous if any
    const { subscribedSessionId } = get()
    if (subscribedSessionId) {
      await get().unsubscribeFromSession()
    }

    set({ subscribedSessionId: sessionId, sessionEvents: [] })

    // Discover agent_id from liveSessions.
    const liveSession = get().liveSessions.find(
      (s) => s.session_id === sessionId || s.id === sessionId
    )
    const agentId = liveSession?.agent_id

    if (!agentId) {
      console.warn(
        '[agentsStore] subscribeToSession: cannot determine agent_id for session',
        sessionId,
        '— ensure fetchLiveSessions() has been called first.',
      )
      return
    }

    // Create a fresh AbortController for this subscription.
    _sessionAbortController = new AbortController()
    const { signal } = _sessionAbortController

    // Fire-and-forget: the subscription runs until aborted or the stream ends.
    agentsApi
      .subscribeToSessionEvents(
        agentId,
        sessionId,
        (event: SessionEvent) => {
          get().addSessionEvent(event)
        },
        signal,
      )
      .catch((err: unknown) => {
        // AbortError is expected on unsubscribe — suppress it.
        if (err instanceof Error && err.name === 'AbortError') return
        console.error('[agentsStore] Session stream unexpectedly closed:', err)
        set({ error: String(err) })
      })
  },

  // Unsubscribe from current session by aborting the SSE stream.
  unsubscribeFromSession: async () => {
    if (_sessionAbortController) {
      _sessionAbortController.abort()
      _sessionAbortController = null
    }
    const { subscribedSessionId } = get()
    if (subscribedSessionId) {
      set({ subscribedSessionId: null, sessionEvents: [] })
    }
  },

  addSessionEvent: (event: SessionEvent) => {
    set((state) => ({
      sessionEvents: [...state.sessionEvents, event],
    }))
  },

  clearSessionEvents: () => {
    set({ sessionEvents: [] })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  clearError: () => {
    set({ error: null })
  },
}))
